import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { useAuth } from "../App";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Checkbox } from "../components/ui/checkbox";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import { ScrollArea } from "../components/ui/scroll-area";
import {
  Leaf, Plus, ArrowLeft, Trash2, Loader2, Package, Check,
  Salad, Flower2, Bug, Home
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Pre-filled seed suggestions by garden type
const SEED_SUGGESTIONS = {
  food: {
    name: "Food Garden",
    icon: <Salad className="w-5 h-5" />,
    color: "text-green-600",
    categories: [
      {
        name: "Vegetables",
        seeds: [
          { name: "Tomato", varieties: ["Cherry", "Beefsteak", "Roma", "Heirloom"] },
          { name: "Pepper", varieties: ["Bell", "Jalapeño", "Cayenne", "Sweet Banana"] },
          { name: "Cucumber", varieties: ["Slicing", "Pickling", "English"] },
          { name: "Zucchini", varieties: ["Green", "Yellow", "Round"] },
          { name: "Squash", varieties: ["Butternut", "Acorn", "Spaghetti"] },
          { name: "Bean", varieties: ["Green", "Pole", "Bush", "Lima"] },
          { name: "Pea", varieties: ["Sugar Snap", "Snow", "Garden"] },
          { name: "Carrot", varieties: ["Nantes", "Danvers", "Rainbow"] },
          { name: "Beet", varieties: ["Detroit Red", "Golden", "Chioggia"] },
          { name: "Lettuce", varieties: ["Romaine", "Butterhead", "Leaf", "Iceberg"] },
          { name: "Spinach", varieties: ["Bloomsdale", "Baby", "Savoy"] },
          { name: "Kale", varieties: ["Curly", "Lacinato", "Red Russian"] },
          { name: "Onion", varieties: ["Yellow", "Red", "White", "Green"] },
          { name: "Garlic", varieties: ["Hardneck", "Softneck", "Elephant"] },
          { name: "Potato", varieties: ["Russet", "Yukon Gold", "Red", "Fingerling"] },
        ]
      },
      {
        name: "Herbs",
        seeds: [
          { name: "Basil", varieties: ["Sweet", "Thai", "Genovese", "Purple"] },
          { name: "Cilantro", varieties: ["Slow-bolt", "Santo"] },
          { name: "Parsley", varieties: ["Italian Flat", "Curly"] },
          { name: "Dill", varieties: ["Bouquet", "Fernleaf"] },
          { name: "Oregano", varieties: ["Greek", "Italian"] },
          { name: "Thyme", varieties: ["Common", "Lemon"] },
          { name: "Mint", varieties: ["Spearmint", "Peppermint"] },
          { name: "Rosemary", varieties: ["Common", "Prostrate"] },
          { name: "Sage", varieties: ["Common", "Purple"] },
          { name: "Chives", varieties: ["Common", "Garlic"] },
        ]
      },
      {
        name: "Fruits",
        seeds: [
          { name: "Strawberry", varieties: ["Everbearing", "June-bearing"] },
          { name: "Melon", varieties: ["Cantaloupe", "Honeydew", "Watermelon"] },
          { name: "Pumpkin", varieties: ["Pie", "Jack-o-lantern", "Mini"] },
        ]
      }
    ]
  },
  serenity: {
    name: "Serenity Garden",
    icon: <Flower2 className="w-5 h-5" />,
    color: "text-blue-600",
    categories: [
      {
        name: "Calming Flowers",
        seeds: [
          { name: "Lavender", varieties: ["English", "French", "Spanish"] },
          { name: "Chamomile", varieties: ["German", "Roman"] },
          { name: "Jasmine", varieties: ["Common", "Star"] },
          { name: "Peony", varieties: ["Herbaceous", "Tree"] },
          { name: "Hydrangea", varieties: ["Mophead", "Lacecap", "Panicle"] },
          { name: "Hosta", varieties: ["Blue", "Green", "Variegated"] },
          { name: "Fern", varieties: ["Boston", "Maidenhair", "Japanese Painted"] },
          { name: "Ornamental Grass", varieties: ["Fountain", "Blue Fescue", "Maiden"] },
        ]
      },
      {
        name: "Fragrant Plants",
        seeds: [
          { name: "Rose", varieties: ["Climbing", "Shrub", "Tea", "Floribunda"] },
          { name: "Gardenia", varieties: ["Common", "Dwarf"] },
          { name: "Lilac", varieties: ["Common", "Dwarf", "Japanese Tree"] },
          { name: "Sweet Pea", varieties: ["Spencer", "Old-fashioned"] },
          { name: "Wisteria", varieties: ["Chinese", "Japanese"] },
          { name: "Honeysuckle", varieties: ["Japanese", "Trumpet"] },
        ]
      },
      {
        name: "Ground Covers",
        seeds: [
          { name: "Creeping Thyme", varieties: ["Red", "White", "Elfin"] },
          { name: "Moss", varieties: ["Irish", "Scotch", "Sheet"] },
          { name: "Sedum", varieties: ["Stonecrop", "Dragon's Blood"] },
          { name: "Ajuga", varieties: ["Burgundy Glow", "Chocolate Chip"] },
        ]
      }
    ]
  },
  pollinator: {
    name: "Pollinator Garden",
    icon: <Bug className="w-5 h-5" />,
    color: "text-amber-600",
    categories: [
      {
        name: "Bee Favorites",
        seeds: [
          { name: "Sunflower", varieties: ["Mammoth", "Teddy Bear", "Autumn Beauty"] },
          { name: "Coneflower", varieties: ["Purple", "Yellow", "White"] },
          { name: "Black-eyed Susan", varieties: ["Rudbeckia", "Gloriosa Daisy"] },
          { name: "Bee Balm", varieties: ["Red", "Pink", "Purple"] },
          { name: "Borage", varieties: ["Blue", "White"] },
          { name: "Clover", varieties: ["White", "Red", "Crimson"] },
          { name: "Salvia", varieties: ["Blue", "Red", "Black & Blue"] },
          { name: "Catmint", varieties: ["Walker's Low", "Six Hills Giant"] },
        ]
      },
      {
        name: "Butterfly Attractors",
        seeds: [
          { name: "Milkweed", varieties: ["Common", "Swamp", "Butterfly"] },
          { name: "Butterfly Bush", varieties: ["Purple", "White", "Pink"] },
          { name: "Zinnia", varieties: ["Giant", "Dwarf", "Cactus"] },
          { name: "Cosmos", varieties: ["Sensation", "Chocolate", "Bright Lights"] },
          { name: "Lantana", varieties: ["Trailing", "Shrub"] },
          { name: "Verbena", varieties: ["Purple", "Red", "White"] },
          { name: "Aster", varieties: ["New England", "Smooth", "Heath"] },
          { name: "Marigold", varieties: ["French", "African", "Signet"] },
        ]
      },
      {
        name: "Hummingbird Plants",
        seeds: [
          { name: "Cardinal Flower", varieties: ["Red", "Blue"] },
          { name: "Trumpet Vine", varieties: ["Orange", "Yellow"] },
          { name: "Columbine", varieties: ["Wild", "McKana Giants"] },
          { name: "Penstemon", varieties: ["Firecracker", "Husker Red"] },
          { name: "Fuchsia", varieties: ["Trailing", "Upright"] },
          { name: "Coral Bells", varieties: ["Palace Purple", "Lime Rickey"] },
        ]
      }
    ]
  },
  freedom: {
    name: "Freedom Garden",
    icon: <Home className="w-5 h-5" />,
    color: "text-purple-600",
    categories: [
      {
        name: "Indoor Edibles",
        seeds: [
          { name: "Microgreens", varieties: ["Sunflower", "Pea Shoots", "Radish", "Broccoli"] },
          { name: "Sprouts", varieties: ["Alfalfa", "Mung Bean", "Lentil", "Clover"] },
          { name: "Lettuce", varieties: ["Butterhead", "Leaf", "Romaine"] },
          { name: "Spinach", varieties: ["Baby", "Bloomsdale"] },
          { name: "Arugula", varieties: ["Wild", "Cultivated"] },
          { name: "Cherry Tomato", varieties: ["Tiny Tim", "Red Robin", "Tumbling Tom"] },
          { name: "Dwarf Pepper", varieties: ["Mini Bell", "Patio Snacker"] },
          { name: "Green Onion", varieties: ["Evergreen", "Tokyo Long White"] },
        ]
      },
      {
        name: "Windowsill Herbs",
        seeds: [
          { name: "Basil", varieties: ["Genovese", "Greek", "Spicy Globe"] },
          { name: "Chives", varieties: ["Common", "Garlic"] },
          { name: "Cilantro", varieties: ["Slow-bolt", "Calypso"] },
          { name: "Parsley", varieties: ["Italian", "Curly"] },
          { name: "Mint", varieties: ["Spearmint", "Peppermint", "Chocolate"] },
          { name: "Oregano", varieties: ["Greek", "Italian"] },
          { name: "Thyme", varieties: ["Common", "Lemon"] },
          { name: "Rosemary", varieties: ["Tuscan Blue", "Prostrate"] },
        ]
      },
      {
        name: "Seed Starting",
        seeds: [
          { name: "Tomato Starts", varieties: ["Beefsteak", "Cherry", "Roma"] },
          { name: "Pepper Starts", varieties: ["Bell", "Hot", "Sweet"] },
          { name: "Eggplant Starts", varieties: ["Black Beauty", "Japanese"] },
          { name: "Squash Starts", varieties: ["Zucchini", "Yellow", "Butternut"] },
          { name: "Melon Starts", varieties: ["Cantaloupe", "Watermelon"] },
          { name: "Cucumber Starts", varieties: ["Slicing", "Pickling"] },
          { name: "Brassica Starts", varieties: ["Broccoli", "Cabbage", "Cauliflower"] },
          { name: "Flower Starts", varieties: ["Marigold", "Zinnia", "Cosmos"] },
        ]
      },
      {
        name: "Container Gardening",
        seeds: [
          { name: "Bush Bean", varieties: ["Provider", "Contender"] },
          { name: "Patio Tomato", varieties: ["Patio Princess", "Bush Early Girl"] },
          { name: "Container Cucumber", varieties: ["Bush Pickle", "Spacemaster"] },
          { name: "Dwarf Carrot", varieties: ["Thumbelina", "Paris Market"] },
          { name: "Radish", varieties: ["Cherry Belle", "French Breakfast"] },
          { name: "Beet", varieties: ["Detroit Dark Red", "Golden"] },
          { name: "Chard", varieties: ["Bright Lights", "Fordhook Giant"] },
          { name: "Kale", varieties: ["Dwarf Blue Curled", "Red Russian"] },
        ]
      }
    ]
  }
};

const SeedStockPage = () => {
  const { user } = useAuth();
  const [seeds, setSeeds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState("my-seeds");
  const [selectedCategory, setSelectedCategory] = useState("food");
  const [selectedSeeds, setSelectedSeeds] = useState({});
  const [addingFromList, setAddingFromList] = useState(false);
  const [newSeed, setNewSeed] = useState({
    plant_name: "",
    variety: "",
    quantity: "",
    source: "",
    notes: ""
  });

  useEffect(() => {
    fetchSeeds();
  }, []);

  const fetchSeeds = async () => {
    try {
      const response = await axios.get(`${API}/seed-stock`, { withCredentials: true });
      setSeeds(response.data);
    } catch (error) {
      console.error("Failed to fetch seeds:", error);
      toast.error("Failed to load seed stock");
    } finally {
      setLoading(false);
    }
  };

  const handleAddSeed = async () => {
    if (!newSeed.plant_name || !newSeed.quantity) {
      toast.error("Please fill in plant name and quantity");
      return;
    }

    setSaving(true);
    try {
      const response = await axios.post(`${API}/seed-stock`, newSeed, { withCredentials: true });
      setSeeds([...seeds, response.data]);
      setDialogOpen(false);
      setNewSeed({ plant_name: "", variety: "", quantity: "", source: "", notes: "" });
      toast.success("Seed added to stock!");
    } catch (error) {
      console.error("Failed to add seed:", error);
      if (error.response?.status === 403) {
        toast.error("Seed stock limit reached. Upgrade for unlimited tracking!");
      } else {
        toast.error("Failed to add seed");
      }
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteSeed = async (seedId) => {
    try {
      await axios.delete(`${API}/seed-stock/${seedId}`, { withCredentials: true });
      setSeeds(seeds.filter(s => s.id !== seedId));
      toast.success("Seed removed from stock");
    } catch (error) {
      console.error("Failed to delete seed:", error);
      toast.error("Failed to remove seed");
    }
  };

  const toggleSeedSelection = (seedName, variety = "") => {
    const key = variety ? `${seedName}-${variety}` : seedName;
    setSelectedSeeds(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  const handleAddSelectedSeeds = async () => {
    const toAdd = Object.entries(selectedSeeds)
      .filter(([_, selected]) => selected)
      .map(([key]) => {
        const parts = key.split('-');
        const plant_name = parts[0];
        const variety = parts.slice(1).join('-') || null;
        return { plant_name, variety };
      });

    if (toAdd.length === 0) {
      toast.error("Please select at least one seed");
      return;
    }

    setAddingFromList(true);
    let added = 0;

    for (const seed of toAdd) {
      try {
        const response = await axios.post(`${API}/seed-stock`, {
          plant_name: seed.plant_name,
          variety: seed.variety,
          quantity: "1 packet",
          source: "",
          notes: ""
        }, { withCredentials: true });
        setSeeds(prev => [...prev, response.data]);
        added++;
      } catch (error) {
        if (error.response?.status === 403) {
          toast.error("Seed stock limit reached. Upgrade for unlimited tracking!");
          break;
        }
      }
    }

    if (added > 0) {
      toast.success(`Added ${added} seed${added > 1 ? 's' : ''} to your stock!`);
      setSelectedSeeds({});
      setActiveTab("my-seeds");
    }
    setAddingFromList(false);
  };

  const isSeedInStock = (seedName, variety = "") => {
    return seeds.some(s => 
      s.plant_name.toLowerCase() === seedName.toLowerCase() &&
      (!variety || s.variety?.toLowerCase() === variety.toLowerCase())
    );
  };

  const selectedCount = Object.values(selectedSeeds).filter(Boolean).length;

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="sticky top-0 z-40 glass border-b border-border/50">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/saved" className="text-muted-foreground hover:text-primary">
              <ArrowLeft className="w-5 h-5" />
            </Link>
            <div>
              <h1 className="font-serif text-xl font-bold">My Seed Stock</h1>
              <p className="text-sm text-muted-foreground">{seeds.length} seed types tracked</p>
            </div>
          </div>
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button className="btn-primary" data-testid="add-seed-btn">
                <Plus className="w-4 h-4 mr-2" />
                Add Custom
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-md">
              <DialogHeader>
                <DialogTitle className="font-serif text-xl">Add Custom Seed</DialogTitle>
              </DialogHeader>
              <div className="space-y-4 mt-4">
                <div>
                  <Label htmlFor="plant_name">Plant Name *</Label>
                  <Input
                    id="plant_name"
                    placeholder="e.g., Tomato"
                    value={newSeed.plant_name}
                    onChange={(e) => setNewSeed({ ...newSeed, plant_name: e.target.value })}
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label htmlFor="variety">Variety</Label>
                  <Input
                    id="variety"
                    placeholder="e.g., Cherokee Purple"
                    value={newSeed.variety}
                    onChange={(e) => setNewSeed({ ...newSeed, variety: e.target.value })}
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label htmlFor="quantity">Quantity *</Label>
                  <Input
                    id="quantity"
                    placeholder="e.g., 50 seeds, 1 packet"
                    value={newSeed.quantity}
                    onChange={(e) => setNewSeed({ ...newSeed, quantity: e.target.value })}
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label htmlFor="source">Source</Label>
                  <Input
                    id="source"
                    placeholder="e.g., Baker Creek, saved from garden"
                    value={newSeed.source}
                    onChange={(e) => setNewSeed({ ...newSeed, source: e.target.value })}
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label htmlFor="notes">Notes</Label>
                  <Input
                    id="notes"
                    placeholder="Any additional notes..."
                    value={newSeed.notes}
                    onChange={(e) => setNewSeed({ ...newSeed, notes: e.target.value })}
                    className="mt-1"
                  />
                </div>
                <Button 
                  onClick={handleAddSeed} 
                  className="w-full btn-primary"
                  disabled={saving}
                >
                  {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                  Add to Stock
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 py-8">
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="mb-6">
            <TabsTrigger value="my-seeds" className="px-6">
              <Package className="w-4 h-4 mr-2" />
              My Seeds ({seeds.length})
            </TabsTrigger>
            <TabsTrigger value="add-from-list" className="px-6">
              <Plus className="w-4 h-4 mr-2" />
              Quick Add from List
            </TabsTrigger>
          </TabsList>

          {/* My Seeds Tab */}
          <TabsContent value="my-seeds">
            {loading ? (
              <div className="flex justify-center py-20">
                <div className="spinner" />
              </div>
            ) : seeds.length === 0 ? (
              <div className="text-center py-20">
                <Package className="w-16 h-16 mx-auto mb-4 text-muted-foreground opacity-50" />
                <h2 className="font-serif text-2xl font-semibold mb-2">No seeds yet</h2>
                <p className="text-muted-foreground mb-6">Start tracking your seed collection</p>
                <Button onClick={() => setActiveTab("add-from-list")} className="btn-primary">
                  <Plus className="w-4 h-4 mr-2" />
                  Add Seeds from List
                </Button>
              </div>
            ) : (
              <Card className="card-organic">
                <CardContent className="p-0">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Plant</TableHead>
                        <TableHead>Variety</TableHead>
                        <TableHead>Quantity</TableHead>
                        <TableHead>Source</TableHead>
                        <TableHead>Notes</TableHead>
                        <TableHead className="w-16"></TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {seeds.map((seed) => (
                        <TableRow key={seed.id}>
                          <TableCell className="font-medium">{seed.plant_name}</TableCell>
                          <TableCell>{seed.variety || "-"}</TableCell>
                          <TableCell>
                            <Badge variant="secondary">{seed.quantity}</Badge>
                          </TableCell>
                          <TableCell className="text-muted-foreground">{seed.source || "-"}</TableCell>
                          <TableCell className="text-muted-foreground text-sm max-w-xs truncate">
                            {seed.notes || "-"}
                          </TableCell>
                          <TableCell>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDeleteSeed(seed.id)}
                              className="text-destructive hover:text-destructive"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* Quick Add from List Tab */}
          <TabsContent value="add-from-list">
            <div className="space-y-6">
              {/* Garden Type Selector */}
              <div className="flex flex-wrap gap-3 justify-center">
                {Object.entries(SEED_SUGGESTIONS).map(([key, garden]) => (
                  <Button
                    key={key}
                    variant={selectedCategory === key ? "default" : "outline"}
                    className={`${selectedCategory === key ? 'bg-primary' : ''}`}
                    onClick={() => setSelectedCategory(key)}
                  >
                    <span className={garden.color}>{garden.icon}</span>
                    <span className="ml-2">{garden.name}</span>
                  </Button>
                ))}
              </div>

              {/* Selected Count & Add Button */}
              {selectedCount > 0 && (
                <motion.div 
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="flex items-center justify-center gap-4 p-4 bg-primary/10 rounded-xl"
                >
                  <span className="font-medium">
                    {selectedCount} seed{selectedCount > 1 ? 's' : ''} selected
                  </span>
                  <Button 
                    onClick={handleAddSelectedSeeds}
                    disabled={addingFromList}
                    className="bg-secondary hover:bg-secondary/90 text-secondary-foreground"
                  >
                    {addingFromList ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <Check className="w-4 h-4 mr-2" />
                    )}
                    Add to My Stock
                  </Button>
                </motion.div>
              )}

              {/* Seed Categories */}
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {SEED_SUGGESTIONS[selectedCategory].categories.map((category) => (
                  <Card key={category.name} className="card-organic">
                    <CardHeader className="pb-3">
                      <CardTitle className="font-serif text-lg">{category.name}</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <ScrollArea className="h-64">
                        <div className="space-y-3">
                          {category.seeds.map((seed) => (
                            <div key={seed.name} className="space-y-2">
                              {/* Main seed checkbox */}
                              <div className="flex items-center gap-2">
                                <Checkbox
                                  id={seed.name}
                                  checked={selectedSeeds[seed.name] || false}
                                  onCheckedChange={() => toggleSeedSelection(seed.name)}
                                  disabled={isSeedInStock(seed.name)}
                                />
                                <label
                                  htmlFor={seed.name}
                                  className={`font-medium cursor-pointer ${
                                    isSeedInStock(seed.name) ? 'text-muted-foreground line-through' : ''
                                  }`}
                                >
                                  {seed.name}
                                  {isSeedInStock(seed.name) && (
                                    <Badge variant="outline" className="ml-2 text-xs">In Stock</Badge>
                                  )}
                                </label>
                              </div>
                              
                              {/* Varieties */}
                              {seed.varieties && (
                                <div className="ml-6 flex flex-wrap gap-2">
                                  {seed.varieties.map((variety) => {
                                    const key = `${seed.name}-${variety}`;
                                    const inStock = isSeedInStock(seed.name, variety);
                                    return (
                                      <label
                                        key={variety}
                                        className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-full text-xs cursor-pointer transition-colors ${
                                          inStock
                                            ? 'bg-muted text-muted-foreground line-through'
                                            : selectedSeeds[key]
                                            ? 'bg-primary/20 text-primary'
                                            : 'bg-muted/50 hover:bg-muted'
                                        }`}
                                      >
                                        <Checkbox
                                          checked={selectedSeeds[key] || false}
                                          onCheckedChange={() => toggleSeedSelection(seed.name, variety)}
                                          disabled={inStock}
                                          className="w-3 h-3"
                                        />
                                        {variety}
                                      </label>
                                    );
                                  })}
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      </ScrollArea>
                    </CardContent>
                  </Card>
                ))}
              </div>

              <p className="text-center text-sm text-muted-foreground">
                Select seeds you already have, then click &quot;Add to My Stock&quot; to track them.
                <br />
                Seeds already in your stock are shown with a strikethrough.
              </p>
            </div>
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
};

export default SeedStockPage;
